// constants.ts

// ============================================================================
// STATUS CONSTANTS
// ============================================================================

// Basic Status
export const CON_STATUS_ZERO: string = "0";
export const CON_STATUS_NONE: string = "";
export const CON_STATUS_ACTIVE: string = "A";
export const CON_STATUS_DELETED: string = "D";
export const CON_STATUS_RECEIVED: string = "R";

// Business Types
export const CON_BUSINESS_TYPECODE: string = "O";
export const CON_STATUS_RESTAURANTS: string = "R";
export const CON_STATUS_KITCHEN: string = "K";

// Generic Status
export const CON_STATUS_NEW: string = "N";
export const CON_STATUS_NO: string = "N";
export const CON_STATUS_YES: string = "Y";

// ============================================================================
// NUMERIC CONSTANTS
// ============================================================================

export const CON_VALUE_ZERO: number = 0;
export const CON_VALUE_ZERO_ST: string = "0.00";
export const CON_EMPTY_VALUE: string = "";

// ============================================================================
// DATE CONSTANTS
// ============================================================================

export const CON_DEFAULT_DATE: string = "0001-01-01";

// ============================================================================
// WORKFLOW STATUS CONSTANTS
// ============================================================================

export const CON_STATUS_SAVED: string = "1";
export const CON_STATUS_PENDING: string = "2";
export const CON_STATUS_SUBMITTED: string = "3";
export const CON_STATUS_APPROVED: string = "4";
export const CON_STATUS_REJECTED: string = "5";
export const CON_STATUS_CANCEL_MODIFY: string = "6";
export const CON_STATUS_RETURN: string = "7";

// Status type for better type safety
export type WorkflowStatus = 
  | typeof CON_STATUS_SAVED
  | typeof CON_STATUS_PENDING
  | typeof CON_STATUS_SUBMITTED
  | typeof CON_STATUS_APPROVED
  | typeof CON_STATUS_REJECTED
  | typeof CON_STATUS_CANCEL_MODIFY
  | typeof CON_STATUS_RETURN;

// Status color mapping
export type StatusColor = "success" | "warning" | "info" | "error" | "secondary" | "primary";

export interface StatusConfig {
  label: string;
  color: StatusColor;
}

export const SUPPLIER_TYPE_MAP: Record<string, StatusConfig> = {
  [CON_STATUS_SAVED]: { label: "Saved", color: "success" },
  [CON_STATUS_PENDING]: { label: "Pending", color: "warning" },
  [CON_STATUS_SUBMITTED]: { label: "Submitted", color: "info" },
  [CON_STATUS_APPROVED]: { label: "Approved", color: "success" },
  [CON_STATUS_REJECTED]: { label: "Rejected", color: "error" },
  [CON_STATUS_CANCEL_MODIFY]: { label: "Cancel/Modify", color: "secondary" },
  [CON_STATUS_RETURN]: { label: "Returned", color: "primary" },
};

// ============================================================================
// GRN STATUS CONSTANTS
// ============================================================================

export const CON_GRN_STATUS_SAVED: string = "2";
export const CON_GRN_STATUS_HOLD: string = "3";
export const CON_GRN_STATUS_SUBMIT: string = "1";
export const CON_GRN_STATUS_APPROVE: string = "4";
export const CON_GRN_STATUS_REVOKE: string = "5";
export const CON_GRN_STATUS_REVERSE: string = "6";
export const CON_GRN_STATUS_RETURN: string = "6";
export const CON_GRN_STATUS_APPROVE_RETURN: string = "8";

export type GrnStatus = 
  | typeof CON_GRN_STATUS_SAVED
  | typeof CON_GRN_STATUS_HOLD
  | typeof CON_GRN_STATUS_SUBMIT
  | typeof CON_GRN_STATUS_APPROVE
  | typeof CON_GRN_STATUS_REVOKE
  | typeof CON_GRN_STATUS_REVERSE
  | typeof CON_GRN_STATUS_RETURN
  | typeof CON_GRN_STATUS_APPROVE_RETURN;

// ============================================================================
// NOTE TYPE CONSTANTS
// ============================================================================

export const CON_NOTE_GRN: string = "GRN";
export const CON_NOTE_RETURN: string = "RN";
export const CON_NOTE_REJECT: string = "REJ";
export const CON_NOTE_DEBIT: string = "DN";
export const CON_NOTE_DELIVERY: string = "DL";

export type NoteType = 
  | typeof CON_NOTE_GRN
  | typeof CON_NOTE_RETURN
  | typeof CON_NOTE_REJECT
  | typeof CON_NOTE_DEBIT
  | typeof CON_NOTE_DELIVERY;

// ============================================================================
// BOOLEAN/FLAG CONSTANTS
// ============================================================================

export const CON_YES: string = "Y";
export const CON_NO: string = "N";

export const CON_IS_CASHIER_YES: string = CON_YES;
export const CON_IS_CASHIER_NO: string = CON_NO;
export const CON_IS_CREDIT_NO: string = CON_NO;

export const CON_DELIVERY_STS: string = CON_YES;

// Category Types
export const CON_CAT_TYPE_RESTAURANT: string = "R";
export const CON_CAT_TYPE_POS: string = "P";

// Menu Type
export const CON_MENU_TYPE_CODE: string = "F";

// ============================================================================
// CASHIER PAGE CONSTANTS
// ============================================================================

export const CON_ADDON_CODE: string = "0";
export const CON_IS_ADDON: string = "N";
export const CON_CODENRATE: string = "0";
export const CON_TAX_PAGE_CODE: string = "IM";
export const CON_TAX_CODE: string = "1017";
export const CON_TAX_RATE: string = "10";
export const CON_TAX_AMOUNT: string = "60";

// ============================================================================
// SYSTEM CONSTANTS
// ============================================================================

export const CON_GUEST_REF_CODE: string = "GREF001";
export const CON_IP_ADDRESS: string = "192.168.1.1";
export const CON_DEF_WAITING_TIME: number = 2000;

// User Page Constants
export const CON_DEFAULT_USER_PW: string = "0";
export const CON_MODULECODE: string = "2";
export const CON_PASSWORD_USER: string = "123";
export const CON_MODULEKEY: string = "BVpFdsp4jqX1r9VdaFEvhN632DxAIbM8";

// ============================================================================
// UI CONSTANTS
// ============================================================================

export const CON_MENUICON_WIDTH: number = 128;
export const CON_MENUICON_HEIGHT: number = 128;

// File Size Limits (in bytes)
export const CON_MAX_FILESIZE_ITEMIMG: number = 1048576; // 1MB
export const CON_MAX_FILESIZE_PROMO: number = 1048576; // 1MB

// ============================================================================
// IMPORT DATA CONSTANTS
// ============================================================================

// File Size Limits
export const CON_MIGRATION_MAX_FILESIZE: number = 10 * 1024 * 1024; // 10MB in bytes
export const CON_MIGRATION_MAX_FILESIZE_MB: number = 10; // 10MB

// Display Constants
export const CON_MIGRATION_PREVIEW_ROW_COUNT: number = 10;
export const CON_MIGRATION_EXCEL_COLUMN_WIDTH: number = 20;
export const CON_MIGRATION_EXCEL_SHEET_NAME: string = "Items";
export const CON_MIGRATION_TEMPLATE_FILENAME: string = "Item_Import_Template.xlsx";

// File Type Constants
export const CON_MIGRATION_EXCEL_MIME_TYPES: string[] = [
  "application/vnd.ms-excel", // .xls
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
];

export const CON_MIGRATION_CSV_MIME_TYPES: string[] = [
  "text/csv",
  "application/csv",
  "text/plain",
];

export const CON_MIGRATION_FILE_MIME_TYPES: string[] = [
  ...CON_MIGRATION_EXCEL_MIME_TYPES,
  ...CON_MIGRATION_CSV_MIME_TYPES,
];

export const CON_MIGRATION_EXCEL_FILE_EXTENSIONS: string = ".xls,.xlsx,.csv";
export const CON_MIGRATION_EXCEL_ACCEPT_TYPES: string = ".xls,.xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv";

// Expected Column Headers
export const CON_MIGRATION_EXPECTED_COLUMNS: string[] = [
  "Brand Code",
  "Main Category Code",
  "Category Code",
  "Sub Category Code",
  "Generic Name Code",
  "Item Name",
  "Model Name",
  "Unit Of Measure Code",
  "Package Type Code",
  "Units Per Package",
  "Description",
  "Variant Data",
];

// Default Values
export const CON_MIGRATION_DEFAULT_VARIANT_VALUE: string = "Standard";
export const CON_MIGRATION_DEFAULT_VARIANT_DESC: string = "Standard item";
export const CON_MIGRATION_DEFAULT_IMAGE_TYPE: string = "common";
export const CON_MIGRATION_DEFAULT_USER: string = "Admin";
export const CON_MIGRATION_DEFAULT_ITEM_CODE: number = 0;
export const CON_MIGRATION_DEFAULT_VARIANT_CODE: number = 0;

// Template Sample Data
export interface MigrationTemplateSample {
  brandName: string;
  mainCategory: string;
  category: string;
  subCategory: string;
  genericName: string;
  itemName: string;
  modelName: string;
  itemSKU: string;
  uom: string;
  packagingType: string;
  unitPerPackage: string;
  description: string;
  isSelling: string;
  isInventory: string;
}

export const CON_MIGRATION_TEMPLATE_SAMPLE: MigrationTemplateSample = {
  brandName: "BRAND001",
  mainCategory: "MAINCAT001",
  category: "CAT001",
  subCategory: "SUBCAT001",
  genericName: "GEN001",
  itemName: "Sample Item Name",
  modelName: "Model XYZ",
  itemSKU: "SKU001",
  uom: "UOM001",
  packagingType: "PKG001",
  unitPerPackage: "10",
  description: "Sample description",
  isSelling: "Yes",
  isInventory: "No",
};

// System Field Definitions
export interface SystemField {
  key: string;
  label: string;
  mandatory: boolean;
  apiField: string;
}

export const CON_MIGRATION_SYSTEM_FIELDS: Record<string, SystemField> = {
  BRAND_NAME: { key: "brandName", label: "Brand Name", mandatory: true, apiField: "brandname" },
  MAIN_CATEGORY: { key: "mainCategory", label: "Item Main Category", mandatory: true, apiField: "maincategory" },
  CATEGORY: { key: "category", label: "Item Category", mandatory: true, apiField: "category" },
  SUB_CATEGORY: { key: "subCategory", label: "Item Subcategory", mandatory: true, apiField: "subcategory" },
  GENERIC_NAME: { key: "genericName", label: "Generic Name", mandatory: true, apiField: "genericname" },
  ITEM_NAME: { key: "itemName", label: "Item Name", mandatory: true, apiField: "itemname" },
  PACKAGING_TYPE: { key: "packagingType", label: "Packaging Type", mandatory: true, apiField: "packagetype" },
  UNIT_PER_PACKAGE: { key: "unitPerPackage", label: "Units per Package", mandatory: true, apiField: "packageunits" },
  IS_SELLING: { key: "isSelling", label: "Selling Item", mandatory: false, apiField: "isselling" },
  IS_INVENTORY: { key: "isInventory", label: "Inventory Item", mandatory: false, apiField: "isinv" },
  UOM: { key: "uom", label: "Unit of Measure (UOM)", mandatory: false, apiField: "measuringunit" },
  MODEL_NAME: { key: "modelName", label: "Model Name", mandatory: false, apiField: "modelname" },
  DESCRIPTION: { key: "description", label: "Description", mandatory: false, apiField: "descrip" },
};

/** Staging flag: duplicate row repeated within the import file. */
export const CON_MIGRATION_DUPLICATE_SOURCE_BATCH: string = "BATCH";
/** Staging flag: duplicate row matches an existing item in the system. */
export const CON_MIGRATION_DUPLICATE_SOURCE_SYSTEM: string = "SYSTEM";

// Batch Processing
export const CON_MIGRATION_BATCH_SIZE: number = 50;

// File Type Options
export const CON_MIGRATION_FILE_TYPE_TEMPLATE: string = "template";
export const CON_MIGRATION_FILE_TYPE_CUSTOM: string = "custom";

export type MigrationFileType = typeof CON_MIGRATION_FILE_TYPE_TEMPLATE | typeof CON_MIGRATION_FILE_TYPE_CUSTOM;

// Starting Point Defaults
export const CON_MIGRATION_DEFAULT_START_ROW: number = 1;
export const CON_MIGRATION_DEFAULT_START_COL: number = 1;
export const CON_MIGRATION_DEFAULT_START_COL_LETTER: string = "A";

// ============================================================================
// ITEM FORM CONSTANTS
// ============================================================================

// Length Limits
export const CON_MAX_ITEM_NAME_LENGTH: number = 150;
export const CON_MAX_MODEL_NAME_LENGTH: number = 150;

export const CON_MAX_NAME_LENGTH: number = 150;
export const CON_MAX_DESCRIPTION_LENGTH: number = 300;

// Default Values
export const CON_DEFAULT_IMAGE_TYPE: string = "common";
export const CON_DEFAULT_ITEM_CODE: number = 0;
export const CON_DEFAULT_VARIANT_VALUE: string = "Standard";
export const CON_DEFAULT_VARIANT_DESC: string = "Standard item";
export const CON_DEFAULT_VARIANT_CODE: number = 0;

// ============================================================================
// VARIANT COMBINATION CONSTANTS
// ============================================================================

export const CON_MAX_VARIANT_COMBINATIONS: number = 64;
export const CON_VARIANT_COMBINATION_WARNING_THRESHOLD: number = 50;

// ============================================================================
// IMAGE VALIDATION CONSTANTS
// ============================================================================

// Dimensions
export const CON_IMAGE_MIN_WIDTH: number = 800;
export const CON_IMAGE_MAX_WIDTH: number = 2000;
export const CON_IMAGE_MIN_HEIGHT: number = 800;
export const CON_IMAGE_MAX_HEIGHT: number = 2000;
export const CON_IMAGE_RECOMMENDED_WIDTH: number = 1000;
export const CON_IMAGE_RECOMMENDED_HEIGHT: number = 1000;

// File Size
export const CON_IMAGE_MIN_FILE_SIZE: number = 1024 * 1024; // 1MB in bytes

// Aspect Ratio
export const CON_IMAGE_ASPECT_RATIO_TOLERANCE: number = 0.05; // ±5% tolerance for 1:1 ratio

// Image validation result interface
export interface ImageValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

// ============================================================================
// EXPORT ALL CONSTANTS AS A SINGLE OBJECT (OPTIONAL)
// ============================================================================

export const Constants = {
  // Status
  STATUS_ZERO: CON_STATUS_ZERO,
  STATUS_NONE: CON_STATUS_NONE,
  STATUS_ACTIVE: CON_STATUS_ACTIVE,
  STATUS_DELETED: CON_STATUS_DELETED,
  STATUS_RECEIVED: CON_STATUS_RECEIVED,
  
  // Workflow
  STATUS_SAVED: CON_STATUS_SAVED,
  STATUS_PENDING: CON_STATUS_PENDING,
  STATUS_SUBMITTED: CON_STATUS_SUBMITTED,
  STATUS_APPROVED: CON_STATUS_APPROVED,
  STATUS_REJECTED: CON_STATUS_REJECTED,
  STATUS_CANCEL_MODIFY: CON_STATUS_CANCEL_MODIFY,
  STATUS_RETURN: CON_STATUS_RETURN,
  
  // GRN Status
  GRN_STATUS_SAVED: CON_GRN_STATUS_SAVED,
  GRN_STATUS_HOLD: CON_GRN_STATUS_HOLD,
  GRN_STATUS_SUBMIT: CON_GRN_STATUS_SUBMIT,
  GRN_STATUS_APPROVE: CON_GRN_STATUS_APPROVE,
  GRN_STATUS_REVOKE: CON_GRN_STATUS_REVOKE,
  GRN_STATUS_REVERSE: CON_GRN_STATUS_REVERSE,
  GRN_STATUS_APPROVE_RETURN: CON_GRN_STATUS_APPROVE_RETURN,
  
  // Notes
  NOTE_GRN: CON_NOTE_GRN,
  NOTE_RETURN: CON_NOTE_RETURN,
  NOTE_REJECT: CON_NOTE_REJECT,
  NOTE_DEBIT: CON_NOTE_DEBIT,
  NOTE_DELIVERY: CON_NOTE_DELIVERY,
  
  // Flags
  YES: CON_YES,
  NO: CON_NO,
  
  // Migration
  MIGRATION: {
    MAX_FILESIZE: CON_MIGRATION_MAX_FILESIZE,
    MAX_FILESIZE_MB: CON_MIGRATION_MAX_FILESIZE_MB,
    PREVIEW_ROW_COUNT: CON_MIGRATION_PREVIEW_ROW_COUNT,
    EXCEL_SHEET_NAME: CON_MIGRATION_EXCEL_SHEET_NAME,
    TEMPLATE_FILENAME: CON_MIGRATION_TEMPLATE_FILENAME,
    BATCH_SIZE: CON_MIGRATION_BATCH_SIZE,
    DEFAULT_VARIANT_VALUE: CON_MIGRATION_DEFAULT_VARIANT_VALUE,
  },
  
  // Item Form
  ITEM: {
    MAX_NAME_LENGTH: CON_MAX_ITEM_NAME_LENGTH,
    MAX_MODEL_NAME_LENGTH: CON_MAX_MODEL_NAME_LENGTH,
    DEFAULT_IMAGE_TYPE: CON_DEFAULT_IMAGE_TYPE,
    DEFAULT_ITEM_CODE: CON_DEFAULT_ITEM_CODE,
  },
  
  // Image Validation
  IMAGE: {
    MIN_WIDTH: CON_IMAGE_MIN_WIDTH,
    MAX_WIDTH: CON_IMAGE_MAX_WIDTH,
    MIN_HEIGHT: CON_IMAGE_MIN_HEIGHT,
    MAX_HEIGHT: CON_IMAGE_MAX_HEIGHT,
    RECOMMENDED_WIDTH: CON_IMAGE_RECOMMENDED_WIDTH,
    RECOMMENDED_HEIGHT: CON_IMAGE_RECOMMENDED_HEIGHT,
    MIN_FILE_SIZE: CON_IMAGE_MIN_FILE_SIZE,
    ASPECT_RATIO_TOLERANCE: CON_IMAGE_ASPECT_RATIO_TOLERANCE,
  },
} as const;